import { BaseErrorMessage, TBaseErrorMessageProps } from '../BaseErrorMessage';
import { BaseInput, TBaseInputProps } from '../BaseInput';
import React, { useState } from 'react';
import { TValidationObject, rules, validateFields } from '../../utils/validation';

import { BaseButton } from '../BaseButton';
import { BaseLink } from '../BaseLink';
import { LoadingWrapper } from '../LoadingWrapper';
import { PASSWORD_MIN_LENGTH } from 'src/constants/application';
import { TFormEditPasswordProps } from './FormEditPassword.types';
import s from './FormEditPassword.module.scss';
import { useGraphQLMessage } from '../../hooks/useGraphQLMessage';
import { useUpdateUserMutation } from '../../graphql';

const initFormData = {
  oldPassword: '',
  newPassword: '',
  repeatedNewPassword: '',
};

export const FormEditPassword: React.FC<TFormEditPasswordProps> = () => {
  const [updateUserMutation, { loading, error }] = useUpdateUserMutation();
  const graphqlMessage = useGraphQLMessage({ error });
  const [formData, setFormData] = useState(initFormData);
  const [passwordSuccessfulChange, setPasswordSuccessfulChange] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    {
      [key in keyof typeof initFormData]?: TBaseErrorMessageProps['children'];
    }
  >({});

  const validators = () => ({
    password: [...((n) => [rules.required(n)])('Password')],
    passwordNew: [
      ...((n) => [rules.required(n), rules.minLength(n, PASSWORD_MIN_LENGTH)])('Password'),
    ],
    passwordRepeated: [
      ...((n) => [
        rules.required(n),
        rules.minLength(n, PASSWORD_MIN_LENGTH),
        rules.passwordMatch(formData.newPassword),
      ])('Password'),
    ],
  });

  const handleFieldChange: TBaseInputProps['onChange'] = ({ value, name }) => {
    setFormData((curr) => ({
      ...curr,
      [name]: value,
    }));
  };

  function handleFieldBlur(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    validationsArr: TValidationObject[],
  ) {
    const { name } = e.target;
    const { errors } = validateFields({
      validators: {
        [name]: validationsArr,
      },
      formData,
    });

    if (errors) {
      if (errors[name]) {
        setValidationErrors((prevState) => ({
          ...prevState,
          [name]: errors[name],
        }));
      }
    } else {
      const newState = { ...validationErrors };
      delete newState[name];
      setValidationErrors(newState);
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const { errors } = validateFields({
      validators: validators(),
      formData,
    });

    setValidationErrors(errors || {});

    // todo - verify check
    if (!errors) {
      return;
    }

    try {
      await updateUserMutation({
        variables: {
          password: formData.newPassword,
          passwordRepeat: formData.repeatedNewPassword,
        },
      });
      setPasswordSuccessfulChange(true);
    } catch (err) {
      setPasswordSuccessfulChange(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={s.formWrapper}>
      <div className={s.formWrapperTop}>
        <BaseLink className={s.editPassword} isActive={true} theme="2">
          Edit Password
        </BaseLink>
        <BaseInput
          type="password"
          name="oldPassword"
          value={formData.oldPassword}
          onChange={handleFieldChange}
          label="Old Password"
          theme="line-light"
          disabled={loading}
          error={validationErrors.oldPassword}
          onBlur={(e) => handleFieldBlur(e, validators().password)}
        />
      </div>
      <div className={s.divider}></div>
      <div className={s.formWrapperBottom}>
        <BaseInput
          type="password"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleFieldChange}
          label="New Password"
          theme="line-light"
          disabled={loading}
          className={s.inputEdit}
          error={validationErrors.newPassword}
          onBlur={(e) => handleFieldBlur(e, validators().passwordNew)}
        />
        <BaseInput
          type="password"
          name="repeatedNewPassword"
          value={formData.repeatedNewPassword}
          onChange={handleFieldChange}
          label="Repeat New Password"
          theme="line-light"
          disabled={loading}
          className={s.inputEdit}
          error={validationErrors.repeatedNewPassword}
          onBlur={(e) => handleFieldBlur(e, validators().passwordRepeated)}
        />
        {graphqlMessage && (
          <BaseErrorMessage className={s.queryMessage}>{graphqlMessage}</BaseErrorMessage>
        )}
        {passwordSuccessfulChange ? (
          <div className={s.successMessage}>Password has been changed</div>
        ) : (
          <LoadingWrapper className={s.loader} loading={loading}>
            {() => (
              <BaseButton type="submit" className={s.buttonLogin}>
                Save
              </BaseButton>
            )}
          </LoadingWrapper>
        )}
      </div>
    </form>
  );
};
